import classNames from "classnames";
import trim from "lodash/trim";
import { Button } from "PFComponents/button";
import DropDown from "PFComponents/dropdown/dropdown";
import { Typography } from "PFComponents/typography";
import { useCurrentAccount } from "PFCore/hooks/queries/account";
import { RecentSearch } from "PFTypes/recent_searches";
import { MutableRefObject } from "react";
import { useTranslation } from "react-i18next";

import { useAutocompleteOptions } from "./hooks/use_autocomplete_options";
import { useDebouncedTerm } from "./hooks/use_debounced_term";
import {
  SEARCH_AUTOCOMPLETE_FOOTER_BUTTON_ID,
  SEARCH_AUTOCOMPLETE_FOOTER_ID
} from "./search_autocomplete.consts";
import css from "./search_autocomplete.module.scss";
import { SearchConfig, SearchDropdownItem, SearchEntityData } from "./search_autocomplete.types";
import { getTermMinLength } from "./search_autocomplete.utils";

type SearchAutocompleteProps = {
  term: string;
  config: SearchConfig;
  recentSearches: RecentSearch[];
  showFooter: boolean;
  dropdownRef: MutableRefObject<null>;
  classes?: {
    dropdown?: string;
  };
  onTermChange: (term: string, entity?: SearchEntityData) => void;
  onClose: VoidFunction;
};

export const SearchAutocomplete = ({
  term,
  config,
  recentSearches,
  showFooter,
  classes = {},
  dropdownRef,
  onTermChange,
  onClose
}: SearchAutocompleteProps) => {
  const { t } = useTranslation();
  const {
    data: {
      config: { minimum_search_term_length }
    }
  } = useCurrentAccount();
  const debouncedTerm = useDebouncedTerm(term);
  const trimmedTerm = trim(debouncedTerm);
  const termMinLength = getTermMinLength(config, minimum_search_term_length);

  const dropdownOptions = useAutocompleteOptions({
    term: trimmedTerm,
    config,
    recentSearches,
    queryEnabled: !!config && trimmedTerm.length >= termMinLength
  });

  const handleChange = ({ text, entity, onClick, useAsTerm }: SearchDropdownItem) => {
    onClick?.(text, entity);
    if (entity && !useAsTerm) {
      onTermChange("", entity);
    } else {
      onTermChange(text, entity);
    }
  };

  return (
    <DropDown
      ref={dropdownRef}
      options={dropdownOptions}
      rootClassName={classNames(css.dropdown, classes.dropdown)}
      // TODO: [PROF-6268] Fix types in DropDown handleChange
      // @ts-ignore - Related to PROF-6268
      handleChange={handleChange}
      handleClose={onClose}
      header={
        trim(term).length < termMinLength ? (
          <Typography
            variant="labelRegular"
            className={classNames(css.termLengthHint, { [css.bottomMargin]: dropdownOptions.length > 0 })}
          >
            {t("typeNCharactersToGetSuggestions", { count: termMinLength })}
          </Typography>
        ) : undefined
      }
      footer={
        showFooter && (
          <footer className={css.footer} id={SEARCH_AUTOCOMPLETE_FOOTER_ID}>
            <Button
              tabIndex={0}
              kind="text"
              onClick={() => onTermChange(term)}
              id={SEARCH_AUTOCOMPLETE_FOOTER_BUTTON_ID}
            >
              {t("seeAllResults")}
            </Button>
          </footer>
        )
      }
    />
  );
};
